import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../../utils/joiConfig';
import {
  Input,
  Text,
  Select,
  Icon,
  Link,
  TextArea,
  FormRow
} from '@efast_public/fjd-component-library';
import { Payment } from '../../api/govos.types';
import { PmPaymentFormProps, PaymentSubFormProps } from './Payment.types';
import { usePaymentForm } from './usePaymentForm';
import { BezahldienstPolicy } from '../../api/models/Bezahldienst/BezahldienstDto';

const getDefaultValues = (
  data?: Payment,
  serviceId?: number
): PmPaymentFormProps => ({
  serviceId: serviceId || 0,
  policy: data?.policy || 'INACTIVE',
  login: data?.paymentService?.params?.login || '',
  password: data?.paymentService?.params?.password || '',
  procedure: data?.paymentService?.params?.procedure || '',
  descript: data?.paymentService?.params?.descript || '',
  accountingRecord: data?.paymentService?.params?.accountingRecord || ''
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>
) =>
  CustomJoi.object<PmPaymentFormProps>({
    serviceId: CustomJoi.number().required(),
    policy: CustomJoi.string().required(),
    login: CustomJoi.string().required(),
    password: CustomJoi.string().required(),
    procedure: CustomJoi.string().required(),
    descript: CustomJoi.string().required(),
    accountingRecord: CustomJoi.string().optional().allow('')
  });

export function PmPaymentForm({
  paymentServices,
  payment,
  parentState,
  ...props
}: PaymentSubFormProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const CustomJoi = createJoiWithTranslations(t);

  const { register, formState, handleSubmit, setValue, watch } =
    useForm<PmPaymentFormProps>({
      defaultValues: getDefaultValues(
        payment,
        parentState.selectedService.serviceId
      ),
      resolver: joiResolver(getValidationSchema(CustomJoi)),
      mode: 'onBlur'
    });

  usePaymentForm({
    paymentServices,
    payment,
    handleSubmit,
    parentState,
    ...props
  });

  const config = watch();

  const policyOptions = [
    {
      value: 'REQUIRED' as BezahldienstPolicy,
      label: t('EditPaymentConfigModal.policyOptions.mandatory')
    },
    {
      value: 'OPTIONAL' as BezahldienstPolicy,
      label: t('EditPaymentConfigModal.policyOptions.optional')
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Policy Selection */}
      <FormRow
        label={t('EditPaymentConfigModal.fields.policy')}
        tooltip={t('EditPaymentConfigModal.fields.policyHint')}
        validationMessage={
          formState.errors.policy?.message
            ? t(formState.errors.policy.message)
            : undefined
        }
        variant={formState.errors.policy ? 'error' : undefined}
      >
        <Select
          value={config.policy}
          onChange={(value) =>
            setValue(
              'policy',
              (value.at(0)?.value || 'INACTIVE') as BezahldienstPolicy,
              {
                shouldValidate: true,
                shouldDirty: true
              }
            )
          }
          options={policyOptions}
          placeholder={t('select.placeholder')}
          style={{ width: '100%' }}
          defaultOptions={policyOptions.filter(
            (o) => o.value === config.policy
          )}
        />
      </FormRow>

      {/* Warning Notice */}
      <div
        style={{
          padding: '12px',
          backgroundColor: '#FFF4E5',
          borderLeft: '4px solid #FF9800',
          borderRadius: '4px'
        }}
      >
        <Text size="s">{t('EditPaymentConfigModal.warningNotice')}</Text>
      </div>

      {/* Gemeindeschlüssel */}
      <FormRow
        label={t('EditPaymentConfigModal.fields.login')}
        tooltip={t('EditPaymentConfigModal.fields.loginHint')}
        validationMessage={
          formState.errors.login?.message
            ? t(formState.errors.login.message)
            : undefined
        }
        variant={formState.errors.login ? 'error' : undefined}
      >
        <Input
          {...register('login')}
          placeholder="01459823"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Password */}
      <FormRow
        label={t('EditPaymentConfigModal.fields.password')}
        tooltip={t('EditPaymentConfigModal.fields.passwordHint')}
        validationMessage={
          formState.errors.password?.message
            ? t(formState.errors.password.message)
            : undefined
        }
        variant={formState.errors.password ? 'error' : undefined}
      >
        <div style={{ position: 'relative' }}>
          <Input
            {...register('password')}
            type={showPassword ? 'text' : 'password'}
            placeholder="*********"
            style={{ width: '100%', paddingRight: '120px' }}
          />
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowPassword(!showPassword);
            }}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <Icon name={showPassword ? 'data-vis-1' : 'data-vis-2'} size="sm" />
            <Text size="s">
              {showPassword
                ? t('EditPaymentConfigModal.actions.hidePassword')
                : t('EditPaymentConfigModal.actions.showPassword')}
            </Text>
          </Link>
        </div>
      </FormRow>

      {/* Vorgang */}
      <FormRow
        label={t('EditPaymentConfigModal.fields.procedure')}
        tooltip={t('EditPaymentConfigModal.fields.procedureHint')}
        validationMessage={
          formState.errors.procedure?.message
            ? t(formState.errors.procedure.message)
            : undefined
        }
        variant={formState.errors.procedure ? 'error' : undefined}
      >
        <Input
          {...register('procedure')}
          placeholder="Test"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Info Notice */}
      <div
        style={{
          padding: '12px',
          backgroundColor: '#E3F2FD',
          borderLeft: '4px solid #2196F3',
          borderRadius: '4px'
        }}
      >
        <Text size="s">
          {t('EditPaymentConfigModal.infoNotice')}
          <Icon
            name="information-outline"
            size="sm"
            style={{ marginLeft: '4px' }}
          />
        </Text>
      </div>

      {/* Verwendungszweck */}
      <FormRow
        label={t('EditPaymentConfigModal.fields.descript')}
        tooltip={t('EditPaymentConfigModal.fields.descriptHint')}
        validationMessage={
          formState.errors.descript?.message
            ? t(formState.errors.descript.message)
            : undefined
        }
        variant={formState.errors.descript ? 'error' : undefined}
      >
        <Input
          {...register('descript')}
          placeholder="Hundesteueranmeldung ${az}"
          style={{ width: '100%' }}
        />
      </FormRow>

      {/* Buchungssatz */}
      <FormRow
        label={t('EditPaymentConfigModal.fields.accountingRecord')}
        tooltip={t('EditPaymentConfigModal.fields.accountingRecordHint')}
        validationMessage={
          formState.errors.accountingRecord?.message
            ? t(formState.errors.accountingRecord.message)
            : undefined
        }
        variant={formState.errors.accountingRecord ? 'error' : undefined}
      >
        <TextArea
          {...register('accountingRecord')}
          rows={4}
          style={{ width: '100%' }}
        />
      </FormRow>
    </div>
  );
}
